import { createContext, useContext } from 'react';
import useMenuItems from '../hooks/useMenuItems';

/**
 * MenuItemsContext - Provides menu items to all components via context
 * Prevents duplicate Firestore listeners by centralizing useMenuItems hook
 */
const MenuItemsContext = createContext(null);

/**
 * MenuItemsProvider - Wraps app to provide menu items via context
 * Calls useMenuItems ONCE and shares result with all consuming components
 * Value is memoized via useMenuItems to prevent unnecessary consumer re-renders
 */
export const MenuItemsProvider = ({ children }) => {
  // useMenuItems already returns a memoized object (via useMemo)
  // so the context value only changes when menu data actually changes
  const menuItemsData = useMenuItems();

  return (
    <MenuItemsContext.Provider value={menuItemsData}>
      {children}
    </MenuItemsContext.Provider>
  );
};

/**
 * useMenuItemsContext - Hook to consume menu items from context
 * Use this instead of calling useMenuItems() directly in components
 */
export const useMenuItemsContext = () => {
  const context = useContext(MenuItemsContext);
  if (!context) {
    throw new Error('useMenuItemsContext must be used within MenuItemsProvider');
  }
  return context;
};
